import "@blissary/design-tokens";
import "@awesome.me/webawesome/dist/styles/themes/default.css";
import "@awesome.me/webawesome/dist/components/button/button.js";
import "@awesome.me/webawesome/dist/components/input/input.js";
import "@awesome.me/webawesome/dist/components/radio-group/radio-group.js";
import { css, html, LitElement, unsafeCSS } from "lit";
import { guard } from "lit/directives/guard.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { repeat } from "lit/directives/repeat.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import "./bl-result-item.js";
import "./bl-load-button.js";
import { iconRemoveShape } from "./bl-icons.js";
import { buildShapeButtonSvg } from "./bliss-svg-utils.js";
import "@awesome.me/webawesome/dist/components/scroller/scroller.js";
import "@blissary/shell-components";
import { get, getAll, getStrokeWidthsForHeight, migrate } from "@blissary/settings";
import {
  closeIcon,
  homeIcon,
  bookOpenIcon,
  penToolIcon,
  downloadIcon,
  globeIcon,
  infoIcon,
  mailIcon,
  searchIcon,
  shapeSearchIcon,
  textSearchIcon,
} from "@blissary/shell-components/icons";
import { BlissColorHelper as BColor } from "@blissary/utils";

/** Escape text so it's safe to embed in HTML. */
function escapeHtml(str) {
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

/** Build an HTML string with search matches wrapped in .highlight spans.
 *  All text segments are HTML-escaped first, so this is safe to use with unsafeHTML. */
function buildHighlightedHtml(text, regex) {
  const parts = [];
  let lastIndex = 0;
  let match;
  regex.lastIndex = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(escapeHtml(text.slice(lastIndex, match.index)));
    }
    parts.push(`<span class="highlight">${escapeHtml(match[0])}</span>`);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(escapeHtml(text.slice(lastIndex)));
  }
  return parts.join("");
}

// Character data is built-in to bliss-svg-builder
// We fetch metadata from server for shape search functionality
let blissData = {};

const appBase = window.location.origin + window.location.pathname; //url app is served from, even when served from a sub directory
const _appBaseCSS = unsafeCSS(appBase); //prepend this in relative css urls for proper finished code.
const _appSrcDirUrl = `${appBase}src/`; //prepend in relative html urls for proper building and proper finished code.

class BlissDictionary extends LitElement {
  static get properties() {
    return {
      //new
      searchString: {
        type: String,
      },
      ongoingSearchString: {
        type: String,
      },
      searchTimeoutHandle: {
        type: Object,
      },
      searchRequestIsOngoing: {
        type: Boolean,
      },
      data: {
        type: Array,
      },
      tempTime: {
        type: Number,
      },
      shapeSearchIsActive: {
        type: Boolean,
      },
      shapeSelectorIsActive: {
        type: Boolean,
      },
      shapeSearchString: {
        type: String,
      },
      shapeSearchCodes: {
        type: Array,
      },
      backgroundColorScheme: {
        type: String,
      },
      additionalLanguage: {
        type: String,
      },

      hasMore: {
        type: Boolean,
      },
      isLoadingMore: {
        type: Boolean,
      },
      maxHitsExceeded: {
        type: Boolean,
      },

      extendedDataIsFetched: {
        type: Boolean,
      },

      exportFormat: {
        type: String,
      },
    };
  }

  constructor() {
    super();
    this.addEventListener("click", this.handleClick);

    this.ongoingSearchString = "";
    this.data = [];
    this.shapeSearchIsActive = false;
    this.shapeSelectorIsActive = false;
    // Read text search from URL early so the input is populated on first render
    const urlQ = new URLSearchParams(window.location.search).get("q");
    this.searchString = urlQ || "";
    this.shapeSearchString = "";
    this.shapeSearchCodes = [];
    this.hasMore = false;
    this.isLoadingMore = false;
    this.maxHitsExceeded = false;

    // SPA navigation: restore search state on browser back/forward
    this._handlePopstate = () => this.setSearchStringFromUrl();
    window.addEventListener("popstate", this._handlePopstate);

    migrate();
    this.backgroundModeColorScheme = get("display.colorScheme");
    this.additionalLanguage = get("display.additionalLanguage");
    this.exportFormat = get("dictionary.format");
    this.selectorShapes = [];

    const _shapeDefinitions = {
      eye: "B303",
    };
    const atomicUnits = [
      //all shapes on /blissmaker + all pointers.
      "B392",
      "B122", //"B338", //house, animal ,fruit,
      "B700",
      "B376",
      "B303",
      "B423", //large wheel, small wheel, head, eye, life
      "B894",
      "B148", //existence, ball,
      //"B597","B281", //sky, earth
      "B473",
      "B374", //nose, hand
      "B500",
      "B312",
      "B455", //parent, father, mother
      "B513", //"B220","B140","B108","B643", //person,child,baby,adult,teenager
      "B433", //"B178","B141", //man, boy, baby boy
      "B710", //"B351","B142", //woman, girl, baby girl
      "B171",
      "B180",
      "B570", //body, loaf of bread, bun
      "B548", //protection
      //"B699","B401", //question mark, question mark (small), intensity, exclamation
      //"B450","B988","B272","B273","B785","B679","B680","B1091","B887","B836","B783","B926","B716","B136","B1101","B989","B1104","B1038", //misc characters with non-vertical or non-horizontal arrows - some should not be atomic
      //"B9","B10","B11","B12","B13","B14","B15","B16","B17","B18","B19","B20","B21","B22","B23","B24","B25","B26","B27","B28" //digits and index numbers
      // "B1023", //oblique square, diamond
    ];

    const groupMap = {
      Xa: "ALPHA",
      Xb: "ALPHA",
      Xc: "ALPHA",
      Xd: "ALPHA",
      Xe: "ALPHA",
      Xf: "ALPHA",
      Xg: "ALPHA",
      Xh: "ALPHA",
      Xi: "ALPHA",
      Xj: "ALPHA",
      Xk: "ALPHA",
      Xl: "ALPHA",
      Xm: "ALPHA",
      Xn: "ALPHA",
      Xo: "ALPHA",
      Xp: "ALPHA",
      Xq: "ALPHA",
      Xr: "ALPHA",
      Xs: "ALPHA",
      Xt: "ALPHA",
      Xu: "ALPHA",
      Xv: "ALPHA",
      Xw: "ALPHA",
      Xx: "ALPHA",
      Xy: "ALPHA",
      Xz: "ALPHA",
      Xå: "ALPHA",
      Xä: "ALPHA",
      Xö: "ALPHA",
      XA: "ALPHA",
      XB: "ALPHA",
      XC: "ALPHA",
      XD: "ALPHA",
      XE: "ALPHA",
      XF: "ALPHA",
      XG: "ALPHA",
      XH: "ALPHA",
      XI: "ALPHA",
      XJ: "ALPHA",
      XK: "ALPHA",
      XL: "ALPHA",
      XM: "ALPHA",
      XN: "ALPHA",
      XO: "ALPHA",
      XP: "ALPHA",
      XQ: "ALPHA",
      XR: "ALPHA",
      XS: "ALPHA",
      XT: "ALPHA",
      XU: "ALPHA",
      XV: "ALPHA",
      XW: "ALPHA",
      XX: "ALPHA",
      XY: "ALPHA",
      XZ: "ALPHA",
      XÅ: "ALPHA",
      XÄ: "ALPHA",
      XÖ: "ALPHA",
      B9: "NUM",
      B10: "NUM",
      B11: "NUM",
      B12: "NUM",
      B13: "NUM",
      B14: "NUM",
      B15: "NUM",
      B16: "NUM",
      B17: "NUM",
      B18: "NUM",
      B19: "NUM",
      B20: "NUM",
      B21: "NUM",
      B22: "NUM",
      B23: "NUM",
      B24: "NUM",
      B25: "NUM",
      B26: "NUM",
      B27: "NUM",
      B28: "NUM",
      QC4NE: "QCNE",
      QC2NE: "QCNE",
      QC1NE: "QCNE",
      "QC.5NE": "QCNE",
      QC4NW: "QCNW",
      QC2NW: "QCNW",
      QC1NW: "QCNW",
      "QC.5NW": "QCNW",
      QC4SE: "QCSE",
      QC2SE: "QCSE",
      QC1SE: "QCSE",
      "QC.5SE": "QCSE",
      QC4SW: "QCSW",
      QC2SW: "QCSW",
      QC1SW: "QCSW",
      "QC.5SW": "QCSW",
      HW8N: "HWN",
      HW4N: "HWN",
      HW2N: "HWN",
      HQW4NW: "HWN",
      HQW2NW: "HWN",
      HQW1NW: "HWN",
      HQW4NE: "HWN",
      HQW2NE: "HWN",
      HQW1NE: "HWN",
      HW8S: "HWS",
      HW4S: "HWS",
      HW2S: "HWS",
      HQW4SW: "HWS",
      HQW2SW: "HWS",
      HQW1SW: "HWS",
      HQW4SE: "HWS",
      HQW2SE: "HWS",
      HQW1SE: "HWS",
      HW8W: "HWW",
      HW4W: "HWW",
      HW2W: "HWW",
      VQW4NW: "HWW",
      VQW2NW: "HWW",
      VQW1NW: "HWW",
      VQW4SW: "HWW",
      VQW2SW: "HWW",
      VQW1SW: "HWW",
      HW8E: "HWE",
      HW4E: "HWE",
      HW2E: "HWE",
      VQW4NE: "HWE",
      VQW2NE: "HWE",
      VQW1NE: "HWE",
      VQW4SE: "HWE",
      VQW2SE: "HWE",
      VQW1SE: "HWE",
      HC8N: "HCN",
      HC4N: "HCN",
      HC2N: "HCN",
      HC1N: "HCN",
      "HC.5N": "HCN",
      HC8S: "HCS",
      HC4S: "HCS",
      HC2S: "HCS",
      HC1S: "HCS",
      "HC.5S": "HCS",
      HC8W: "HCW",
      HC4W: "HCW",
      HC2W: "HCW",
      HC1W: "HCW",
      "HC.5W": "HCW",
      HC8E: "HCE",
      HC4E: "HCE",
      HC2E: "HCE",
      HC1E: "HCE",
      "HC.5E": "HCE",
      W8W: "WWE",
      W8E: "WWE",
      W8N: "WNS",
      W8S: "WNS",
      B171: "BODY",
      B180: "BODY",
      B570: "BODY",
      DOT: "DOTCOM",
      COMMA: "DOTCOM",
      OC8NW: "OC",
      OC4NW: "OC",
      OC2NW: "OC",
      OC1NW: "OC",
      OC8NE: "OC",
      OC4NE: "OC",
      OC2NE: "OC",
      OC1NE: "OC",
      OC8SW: "OC",
      OC4SW: "OC",
      OC2SW: "OC",
      OC1SW: "OC",
      OC8SE: "OC",
      OC4SE: "OC",
      OC2SE: "OC",
      OC1SE: "OC",
      R84: "HR",
      R42: "HR",
      R48: "VR",
      R24: "VR",
      DS8: "IS",
      DS4: "IS",
      DS2: "IS",
      B1023: "IS", //irregular square
      P4W: "P",
      P4E: "P",
      P4N: "P",
      P4S: "P",
      P3W: "P",
      P3E: "P",
      P3N: "P",
      P3S: "P",
      P2W: "P",
      P2E: "P",
      P2N: "P",
      P2S: "P",
      P3NW: "P",
      P3NE: "P",
      P3SW: "P",
      P3SE: "P",
      P2NW: "P",
      P2NE: "P",
      P2SW: "P",
      P2SE: "P",
      "P1.5NW": "P",
      "P1.5NE": "P",
      "P1.5SW": "P",
      "P1.5SE": "P",
      //B220: "CHILD", B140: "CHILD",
      //B178: "BOY", B141: "BOY",
      //B351: "GIRL", B142: "GIRL",
      B81: "IND",
      B82: "IND",
      B83: "IND",
      B84: "IND",
      B85: "IND",
      B86: "IND",
      B87: "IND",
      B88: "IND",
      B89: "IND",
      B90: "IND",
      B91: "IND",
      B92: "IND",
      B93: "IND",
      B94: "IND",
      B95: "IND",
      B96: "IND",
      B97: "IND",
      B98: "IND",
      B99: "IND",
      B902: "IND",
      B903: "IND",
      B904: "IND",
      B905: "IND",
      B906: "IND",
      B907: "IND",
      B908: "IND",
      B909: "IND",
      B910: "IND",
      B911: "IND",
      B912: "IND",
      B913: "IND",
      B914: "IND",
      B915: "IND",
      B916: "IND",
      B928: "IND",
      B992: "IND",
      B5996: "IND",
      B5997: "IND",
      B5998: "IND",
      OS8N: "OS",
      OS8E: "OS",
      OS8S: "OS",
      OS8W: "OS",
      OS4N: "OS",
      OS4E: "OS",
      OS4S: "OS",
      OS4W: "OS",
      OS2N: "OS",
      OS2E: "OS",
      OS2S: "OS",
      OS2W: "OS",
      OR84N: "WOR",
      OR84S: "WOR",
      OR48E: "WOR",
      OR48W: "WOR",
      OR42N: "WOR",
      OR42S: "WOR",
      OR24E: "WOR",
      OR24W: "WOR", //wide open rectangle
      OR48N: "NOR",
      OR48S: "NOR",
      OR84E: "NOR",
      OR84W: "NOR",
      OR24N: "NOR",
      OR24S: "NOR",
      OR42E: "NOR",
      OR42W: "NOR", //narrow open rectangle
      VL12: "LVL",
      VL10: "LVL",
      VL8: "LVL",
      VL6: "LVL", // long vertical line
      VL4: "SVL",
      VL3: "SVL",
      VL2: "SVL",
      "VL1.5": "SVL",
      "VL1.25": "SVL",
      VL1: "SVL",
      "VL.5": "SVL",
      "VL.25": "SVL", //short vertical line
      HL16: "LHL",
      HL15: "LHL",
      HL14: "LHL",
      HL12: "LHL",
      HL10: "LHL",
      HL8: "LHL",
      HL6: "LHL", //long horizontal line
      HL4: "SHL",
      HL3: "SHL",
      HL2: "SHL",
      "HL1.5": "SHL",
      HL1: "SHL",
      "HL.5": "SHL",
      "HL.25": "SHL",
      SC8: "SC",
      SC4: "SC",
      SC2: "SC",
      DL12N: "DLN",
      DL8N: "DLN",
      DL6N: "DLN",
      DL5N: "DLN",
      DL4N: "DLN",
      DL3N: "DLN",
      DL2N: "DLN",
      "DL1.5N": "DLN",
      DL1N: "DLN",
      "DL.5N": "DLN",
      "DL2-3N": "DLN",
      "DL1-1.5N": "DLN",
      "DL3-2N": "DLN",
      "DL1.5-1N": "DLN",
      "DL4-8N": "DLN",
      "DL2-4N": "DLN",
      "DL1-2N": "DLN",
      "DL.5-1N": "DLN",
      "DL8-4N": "DLN",
      "DL4-2N": "DLN",
      "DL2-1N": "DLN",
      "DL1-.5N": "DLN",
      "DL1-4N": "DLN",
      "DL.5-2N": "DLN",
      "DL4-1N": "DLN",
      "DL2-.5N": "DLN",
      DLIC8N: "DLN",
      DLIC4N: "DLN",
      DLOC8NE: "DLN",
      DLOC8SW: "DLN",
      DLOC4NE: "DLN",
      DLOC4SW: "DLN",
      DL12S: "DLS",
      DL8S: "DLS",
      DL6S: "DLS",
      DL5S: "DLS",
      DL4S: "DLS",
      DL3S: "DLS",
      DL2S: "DLS",
      "DL1.5S": "DLS",
      DL1S: "DLS",
      "DL.5S": "DLS",
      "DL2-3S": "DLS",
      "DL1-1.5S": "DLS",
      "DL3-2S": "DLS",
      "DL1.51S": "DLS",
      "DL4-8S": "DLS",
      "DL2-4S": "DLS",
      "DL1-2S": "DLS",
      "DL.5-1S": "DLS",
      "DL8-4S": "DLS",
      "DL4-2S": "DLS",
      "DL2-1S": "DLS",
      "DL1-.5S": "DLS",
      "DL1-4S": "DLS",
      "DL.5-2S": "DLS",
      "DL4-1S": "DLS",
      "DL2-.5S": "DLS",
      DLIC8S: "DLS",
      DLIC4S: "DLS",
      DLOC8NW: "DLS",
      DLOC8SE: "DLS",
      DLOC4NW: "DLS",
      DLOC4SE: "DLS",
      DC8: "DC",
      DC4: "DC",
      DC2: "DC",
      B622: "SDC", //straight & diagonal cross (incl. star)
      B894: "CWVL", //circle with vertical line
      RA8NW: "RANW",
      RA4NW: "RANW",
      RA3NW: "RANW",
      RA2NW: "RANW",
      "RA1.5NW": "RANW",
      RA1NW: "RANW",
      RA8NE: "RANE",
      RA4NE: "RANE",
      RA3NE: "RANE",
      RA2NE: "RANE",
      "RA1.5NE": "RANE",
      RA1NE: "RANE",
      RA8SW: "RASW",
      RA4SW: "RASW",
      RA3SW: "RASW",
      RA2SW: "RASW",
      "RA1.5SW": "RASW",
      RA1SW: "RASW",
      RA8SE: "RASE",
      RA4SE: "RASE",
      RA3SE: "RASE",
      RA2SE: "RASE",
      "RA1.5SE": "RASE",
      RA1SE: "RASE",
      RA8N: "AN",
      RA4N: "AN",
      RA3N: "AN",
      RA2N: "AN",
      RA1N: "AN",
      AA8N: "AN",
      AA4N: "AN",
      AA2N: "AN",
      RA8S: "AS",
      RA4S: "AS",
      RA3S: "AS",
      RA2S: "AS",
      RA1S: "AS",
      AA8S: "AS",
      AA4S: "AS",
      AA2S: "AS",
      RA8W: "AW",
      RA4W: "AW",
      RA3W: "AW",
      RA2W: "AW",
      RA1W: "AW",
      AA8W: "AW",
      AA4W: "AW",
      AA2W: "AW",
      RA8E: "AE",
      RA4E: "AE",
      RA3E: "AE",
      RA2E: "AE",
      RA1E: "AE",
      AA8E: "AE",
      AA4E: "AE",
      AA2E: "AE",
      RT8NW: "DRT",
      RT4NW: "DRT",
      RT2NW: "DRT", //diagonal right triangle
      RT8NE: "DRT",
      RT4NE: "DRT",
      RT2NE: "DRT",
      RT8SW: "DRT",
      RT4SW: "DRT",
      RT2SW: "DRT",
      RT8SE: "DRT",
      RT4SE: "DRT",
      RT2SE: "DRT",
      RT8N: "TN",
      RT4N: "TN",
      RT2N: "TN",
      AT8N: "TN",
      AT4N: "TN",
      AT2N: "TN",
      RT8S: "TS",
      RT4S: "TS",
      RT2S: "TS",
      AT8S: "TS",
      AT4S: "TS",
      AT2S: "TS",
      RT8W: "TW",
      RT4W: "TW",
      RT2W: "TW",
      AT8W: "TW",
      AT4W: "TW",
      AT2W: "TW",
      RT8E: "TE",
      RT4E: "TE",
      RT2E: "TE",
      AT8E: "TE",
      AT4E: "TE",
      AT2E: "TE",
      LARR8N: "ARRN",
      LARR4N: "ARRN",
      ARR8N: "ARRN",
      ARR4N: "ARRN",
      LARR8S: "ARRS",
      LARR4S: "ARRS",
      ARR8S: "ARRS",
      ARR4S: "ARRS",
      LARR8W: "ARRW",
      LARR4W: "ARRW",
      ARR8W: "ARRW",
      ARR4W: "ARRW",
      LARR8E: "ARRE",
      LARR4E: "ARRE",
      ARR8E: "ARRE",
      ARR4E: "ARRE",
      LARR8NW: "DARR",
      LARR4NW: "DARR",
      ARR8NW: "DARR",
      ARR4NW: "DARR", //Diagonal Arrow
      LARR8NE: "DARR",
      LARR4NE: "DARR",
      ARR8NE: "DARR",
      ARR4NE: "DARR",
      LARR8SW: "DARR",
      LARR4SW: "DARR",
      ARR8SW: "DARR",
      ARR4SW: "DARR",
      LARR8SE: "DARR",
      LARR4SE: "DARR",
      ARR8SE: "DARR",
      ARR4SE: "DARR",
      LARRC8EC: "RARR",
      ARRC8EC: "RARR",
      LARRC8WC: "RARR",
      ARRC8WC: "RARR",
      LARRC8NC: "RARR",
      ARRC8NC: "RARR",
      LARRC8SC: "RARR",
      ARRC8SC: "RARR",
      LARRC8ECC: "RARR",
      ARRC8ECC: "RARR",
      LARRC8WCC: "RARR",
      ARRC8WCC: "RARR",
      LARRC8NCC: "RARR",
      ARRC8NCC: "RARR",
      LARRC8SCC: "RARR",
      ARRC8SCC: "RARR",
      LARROC8SEC: "RARR",
      ARROC8SEC: "RARR",
      LARROC8NWC: "RARR",
      ARROC8NWC: "RARR",
      LARROC8NEC: "RARR",
      ARROC8NEC: "RARR",
      LARROC8SWC: "RARR",
      ARROC8SWC: "RARR",
      LARROC8SECC: "RARR",
      ARROC8SECC: "RARR",
      LARROC8NWCC: "RARR",
      ARROC8NWCC: "RARR",
      LARROC8NECC: "RARR",
      ARROC8NECC: "RARR",
      LARROC8SWCC: "RARR",
      ARROC8SWCC: "RARR",
      LARRHC8NC: "RARR",
      ARRHC8NC: "RARR",
      LARRHC8SC: "RARR",
      ARRHC8SC: "RARR",
      LARRHC8WC: "RARR",
      ARRHC8WC: "RARR",
      LARRHC8EC: "RARR",
      ARRHC8EC: "RARR",
      LARRHC8NCC: "RARR",
      ARRHC8NCC: "RARR",
      LARRHC8SCC: "RARR",
      ARRHC8SCC: "RARR",
      LARRHC8WCC: "RARR",
      ARRHC8WCC: "RARR",
      LARRHC8ECC: "RARR",
      ARRHC8ECC: "RARR",
      LARRQC4NWC: "RARR",
      ARRQC4NWC: "RARR",
      LARRQC4SWC: "RARR",
      ARRQC4SWC: "RARR",
      LARRQC4NEC: "RARR",
      ARRQC4NEC: "RARR",
      LARRQC4SEC: "RARR",
      ARRQC4SEC: "RARR",
      LARRQC4NWCC: "RARR",
      ARRQC4NWCC: "RARR",
      LARRQC4SWCC: "RARR",
      ARRQC4SWCC: "RARR",
      LARRQC4NECC: "RARR",
      ARRQC4NECC: "RARR",
      LARRQC4SECC: "RARR",
      ARRQC4SECC: "RARR",
      S4: "SS",
      S2: "SS", //Small Square
      C2: "SMC",
      C1: "SMC", //Small Circle
      B3: "QM",
      B699: "QM", //question mark

      //B988 NOT RANE or RASW

      //RA3NW RA1.5NW RA1NW not needed for now
    };

    fetch(`${appBase}api/blissdata`)
      .then((x) => x.json())
      .then((data) => {
        const unnestleCode = (singleCode) => {
          if (groupMap[singleCode]) return groupMap[singleCode];
          //om singleCode ÄR en atomicUnit
          if (atomicUnits.indexOf(singleCode) !== -1) return singleCode;
          //om singleCode INTE startar med B
          if (singleCode[0] !== "B") return singleCode;
          return data[singleCode].code.split(";").map((item) => {
            return unnestleCode(item.split(":")[0]);
          });
        };

        //var t0 = performance.now();
        for (const bCharKey in data) {
          const bChar = data[bCharKey]; //"B414;B399" => [["B414"],[["B12"], ["B940"]] --- ["B414",["B12", "B940"]]
          let bCharCodes = [];
          if (groupMap[bCharKey]) {
            bCharCodes.push(groupMap[bCharKey]);
          } else if (atomicUnits.indexOf(bCharKey) !== -1) {
            bCharCodes.push(bCharKey);
          } else if (bChar.code) {
            bCharCodes = bChar.code.split(";").map((item) => {
              return unnestleCode(item.split(":")[0]);
            });
          }
          this.flattenedBCharCodes = bCharCodes.flat(10);
          bChar.shapeSearchCode = this.flattenedBCharCodes.join("|");
          //console.log("flattened B Char Codes");
          //console.log(this.flattenedBCharCodes);

          //console.log(bCharCodes);
          //.shapeSearchCode = //new BlissText() --nej vi behöver inte använda oss av BlissText ö h t
        }
        //var t1 = performance.now();

        data.B988.shapeSearchCode = "DARR|DARR";
        data.B272.shapeSearchCode = "DARR";
        data.B273.shapeSearchCode = "DARR";
        data.B679.shapeSearchCode = "DARR";
        data.B680.shapeSearchCode = "DARR";
        data.B103.shapeSearchCode = "HCS|NOR";
        data.B104.shapeSearchCode = ["LVL|SHL|SHL", "SC|SHL"];
        //data.B118.shapeSearchCode = "ALPHA|ALPHA|ALPHA";
        data.B133.shapeSearchCode = "LVL|SHL|SHL|LVL";
        data.B156.shapeSearchCode = ["LVL|LHL|DLN|LVL", "LVL|LHL|DRT|LVL"];
        data.B165.shapeSearchCode = ["BODY|C4|SVL|SHL|SVL|ARRS", "HWW|LHL|SHL|SHL|HWE|C4|SVL|SHL|SVL|ARRS"];
        data.B173.shapeSearchCode = ["VR|WOR", "VR|VR", "S8|LVL"];
        data.B183.shapeSearchCode = ["WOR|LVL", "SVL|SC|SVL", "SVL|LVL|SVL|LHL"];
        data.B184.shapeSearchCode = ["WOR|LVL", "SVL|SC|SVL", "SVL|LVL|SVL|LHL"];
        data.B189.shapeSearchCode = "WOR|LHL|B700|B700|B700|DLN|DLS|LHL";
        data.B191.shapeSearchCode = "CWVL|CWVL|HCS";
        data.B199.shapeSearchCode = "B392|B700|B700";
        data.B205.shapeSearchCode = "HR|C4|LHL|C4";
        data.B219.shapeSearchCode = ["S8|LHL|DOTCOM|DOTCOM", "HR|HR|DOTCOM|DOTCOM", "HR|WOR|DOTCOM|DOTCOM"];
        data.B252.shapeSearchCode = ["S8|DOTCOM|LVL|DOTCOM", "VR|VR|DOTCOM|DOTCOM", "VR|WOR|DOTCOM|DOTCOM"];
        data.B231.shapeSearchCode = ["WOR|SVL", "SVL|SVL|SVL|LHL"];
        data.B235.shapeSearchCode = ["HCN|HCN|OS|LVL", "HCN|HCN|LVL|LVL|LVL|LHL"];
        data.B237.shapeSearchCode = "B548|HCS";
        data.B243.shapeSearchCode = ["LHL|LVL|OS", "LHL|LVL|SS", "LHL|SVL|SS", "LHL|LVL|SHL|SHL|SVL"];
        data.B248.shapeSearchCode = ["LVL|SHL", "SC"];
        data.B250.shapeSearchCode = ["AN|SS|AS", "TN|SVL|SVL|TS"];
        data.B274.shapeSearchCode = ["S8|LHL|DOTCOM|DOTCOM|P", "HR|HR|DOTCOM|DOTCOM|P", "HR|WOR|DOTCOM|DOTCOM|P"];
        data.B284.shapeSearchCode = ["AE|WOR", "DRT|DRT"];
        data.B334.shapeSearchCode = ["DLN|DLS|DLN|DLN", "WOR|DLN"];
        data.B355.shapeSearchCode = ["AN|SHL|SHL|TN", "TN|TN"];
        data.B370.shapeSearchCode = ["WNS|SS|ARRS", "WNS|LVL|OS|TS"];
        data.B421.shapeSearchCode = ["S8|AS", "TS|OS"];
        data.B443.shapeSearchCode = [
          "SVL|SVL|SVL|SVL|LVL|SVL|SVL|SVL|SVL|LHL|SHL",
          "WOR|SVL|SVL|SVL|SVL|SVL|SVL|LHL|SHL",
        ];
        data.B444.shapeSearchCode = ["C4|LVL|SHL", "C4|B513"];
        data.B499.shapeSearchCode = ["HR|AE|AW", "LHL|TE|TW|LHL"];
        data.B517.shapeSearchCode = ["SVL|SHL|SVL", "RASW|SVL", "SVL|RANE"];
        data.B522.shapeSearchCode = ["LVL|LHL|DLN|LVL|P", "LVL|LHL|DRT|LVL|P"];
        data.B534.shapeSearchCode = "SVL|LHL|SVL|LHL|B700|B700|LHL";
        data.B541.shapeSearchCode = ["B291|RASW", "B291|SS"];
        data.B566.shapeSearchCode = ["RASW|RANE", "RASW|SS"];
        data.B575.shapeSearchCode = ["LVL|DLS|SHL", "LVL|DRT", "SVL|DRT"];
        data.B576.shapeSearchCode = ["SVL|LHL|HCS|DLS", "LHL|HCS|DRT"];
        data.B579.shapeSearchCode = ["DLN|RASE|RASE", "DLN|DRT|DRT"];
        data.B587.shapeSearchCode = ["OS|LHL", "WOR|WOR", "HR|WOR"];
        data.B598.shapeSearchCode = ["LHL|QCSE|SS", "LHL|QCSE|OS"];
        data.B604.shapeSearchCode = ["WNS|LVL|SDC", "WNS|LVL|DC"];
        data.B605.shapeSearchCode = ["WNS|LVL|SDC|P", "WNS|LVL|DC|P"];
        data.B606.shapeSearchCode = ["SS|SS", "SC|RASW|RANE"];
        data.B614.shapeSearchCode = "DLN|C4";
        data.B619.shapeSearchCode = ["RANW|RANW", "SVL|RASE|SHL"];
        data.B620.shapeSearchCode = ["RANE|RASW", "RANE|SS"];
        data.B621.shapeSearchCode = ["TN|TS", "TN|TS|DRT|DRT|DRT|DRT"];
        data.B212.shapeSearchCode = ["LVL|SHL|SVL", "LVL|RANE"];
        data.B659.shapeSearchCode = ["WNS|LVL|SHL|SVL", "WNS|LVL|RANE"];
        data.B701.shapeSearchCode = ["LVL|SHL|SVL|B700", "LVL|RANE|B700"];
        data.B664.shapeSearchCode = ["B700|SHL|B700|DLN|DLS", "C8|DC|SHL|B700|DLN|DLS"];
        data.B668.shapeSearchCode = ["LHL|B700|B700|B700|DLN|DLS|OS", "LHL|B700|B700|B700|DLN|DLS|SS"];
        data.B683.shapeSearchCode = ["DRT|LHL|DRT|P", "WOR|DLS|P|DLN"];
        data.B692.shapeSearchCode = ["SDC|LVL", "DC|LVL"];
        data.B706.shapeSearchCode = ["S8|SC", "SS|SS|SS|SS"];
        data.B707.shapeSearchCode = ["S8|LVL|LVL|LHL|LHL", "SS|SS|SS|SS|SS|WR|WR|WR|WR"];
        data.B717.shapeSearchCode = ["AS|SHL|SHL|SVL", "AS|SHL|SC"];
        data.B720.shapeSearchCode = ["LVL|DLN|SHL", "DRT|SVL", "DRT|LVL"];
        data.B721.shapeSearchCode = ["SVL|LHL|HCS|DLN", "DRT|LHL|HCS", "DRT|SHL|HCS"];
        data.B725.shapeSearchCode = ["H|AN|SHL|SHL|TN", "H|TN|TN"];
        data.B734.shapeSearchCode = ["P|AN|SHL|SHL|TN|P", "P|TN|TN|P"];
        data.B744.shapeSearchCode = "HR|LHL|SVL";
        data.B750.shapeSearchCode = "SHL|AN|SHL";
        data.B755.shapeSearchCode = ["NOR|LVL", "SHL|LVL|LVL|LVL"];
        data.B762.shapeSearchCode = ["LVL|DLN|SHL|P", "DRT|SVL|P", "DRT|LVL|P"];
        data.B763.shapeSearchCode = ["LVL|DLN|SHL|P", "DRT|SVL|P", "DRT|LVL|P"];
        data.B772.shapeSearchCode = ["HCS|LHL|SVL|RANW", "HCS|LHL|OS", "HCS|LHL|SS", "HCS|SHL|SS"];
        data.B781.shapeSearchCode = ["HR|AE|AW", "TE|LHL|LHL|TW"];
        data.B806.shapeSearchCode = ["B392|WOR|P", "B392|HR|P"];
        data.B1131.shapeSearchCode = ["B392|WOR", "B392|HR"];
        data.B811.shapeSearchCode = ["RASW|RANE|P", "RASW|SS|P"];
        data.B822.shapeSearchCode = ["RASW|RANE|P", "RASW|SS|P"];
        data.B814.shapeSearchCode = ["HR|SVL", "SS|SS"];
        data.B816.shapeSearchCode = ["B392|LVL|B548|LHL|LVL|SVL", "B392|B392|LVL|LVL"];
        data.B819.shapeSearchCode = "LHL|LHL|LVL|CWVL|CWVL|HCS";
        data.B827.shapeSearchCode = ["C8|DC", "B700"];
        data.B830.shapeSearchCode = ["LHL|B122|SVL|SHL", "SHL|B122|SVL|SHL"];
        data.B864.shapeSearchCode = ["HR|SVL|SVL|SVL", "VR|VR|VR|VR"];
        data.B890.shapeSearchCode = ["WNS|HWN", "HWN|DHWNS"];
        data.B899.shapeSearchCode = ["VR|SHL", "SS|SS"];
        data.B623.shapeSearchCode = ["RANW|RANW|P", "SVL|RASE|SHL|P"];
        data.B950.shapeSearchCode = ["S8|LVL|LVL|LVL|LHL|LHL|LHL", "SS|SS|SS|SS|SS|SS|SS|SS|SS|SS|SS|SS|SS|SS|SS|SS"];
        data.B953.shapeSearchCode = ["SHL|SC", "SHL|SHL|SVL"];
        data.B959.shapeSearchCode = ["LHL|SC", "LHL|LHL|LVL"];
        //data.B972.shapeSearchCode = "ALPHA|ALPHA|ALPHA";
        data.B981.shapeSearchCode = ["P|HR|SVL|HWN|HWN|HWS|HWS|P", "P|SS|SS|HWN|HWN|HWS|HWS|P"];
        data.B982.shapeSearchCode = ["HR|SVL|HWN|HWN|HWS|HWS", "SS|SS|HWN|HWN|HWS|HWS"];
        data.B989.shapeSearchCode = "RARR|RARR";
        data.B993.shapeSearchCode = "BODY|DOTCOM";
        data.B1029.shapeSearchCode = "HCS|ARRS|ARRN";
        data.B1059.shapeSearchCode = "C4|B513|QCNE|QCNW";
        data.B1068.shapeSearchCode = "SDC|C8";
        data.B1105.shapeSearchCode = "SDC|S8";
        data.B1077.shapeSearchCode = ["SVL|SHL|SVL|ARRE", "RASW|SVL|ARRE", "SVL|RANE|ARRE"];
        data.B1083.shapeSearchCode = ["SVL|SHL|SVL|LHL|QCSE", "RASW|SVL|LHL|QCSE", "SVL|RANE|LHL|QCSE"];
        data.B1081.shapeSearchCode = ["SVL|SHL|SVL|SVL", "OS|SVL"];
        data.B1086.shapeSearchCode = ["C4|LHL|SVL|WWE|SVL", "C4|LHL|OS|WWE"];
        data.B1088.shapeSearchCode = ["AN|SS", "TN|SS", "TN|OS"];
        data.B1126.shapeSearchCode = ["DLS|AW|DLN", "AE|AE"];
        data.B1144.shapeSearchCode = ["DRT|LHL|DRT", "DLS|WOR|DLN"];
        data.B1147.shapeSearchCode = "P";
        data.B5841.shapeSearchCode = "OS|CWVL|CWVL|HCS";
        data.B1.shapeSearchCode = "";
        data.B322.shapeSearchCode = ["LVL|OS", "LVL|SS", "SVL|SS", "LVL|SHL|SHL|SVL"];

        data.B451.shapeSearchCode = "CWVL|CWVL";
        data.B682.shapeSearchCode = ["BODY|TN|P", "HWW|LHL|SHL|SHL|HWE|TN|P"];
        data.B820.shapeSearchCode = "HCN|CWVL|CWVL|ARRE";

        //crosses
        data.B381.shapeSearchCode = ["DLN|DLS|SVL|B700", "DC|SVL|B700"];
        data.B411.shapeSearchCode = ["DLS|DLN", "DC"];
        data.B536.shapeSearchCode = ["DLS|DLN|LHL", "DC|LHL"];
        data.B538.shapeSearchCode = ["DLS|DLN|P", "DC|P"];
        data.B693.shapeSearchCode = ["DLS|DLN|DLN|DLS", "DC|DC|DC"];
        data.B971.shapeSearchCode = ["DLS|DLN|C4", "DC|C4"];
        data.B983.shapeSearchCode = ["DLN|DLS|SVL", "DC|SVL"];
        data.B555.shapeSearchCode = ["DLN|C4|SC", "DLN|B700"];
        data.B1016.shapeSearchCode = ["C4|SC|DLS|DLN|C4|SC", "C4|SC|DC|C4|SC", "B700|DLS|DLN|B700", "B700|DC|B700"];

        //double half-wave west-east
        data.B138.shapeSearchCode = "QCNW|HWW|HWE|HWW|HWE|SHL|DHWWE";
        data.B187.shapeSearchCode = "LHL|C4|SVL|C4|DHWWE|P";
        data.B193.shapeSearchCode = "DHWWE|DHWWE";
        data.B217.shapeSearchCode = "C4|DHWWE|QCNW|C4|HWW|DHWWE";
        data.B296.shapeSearchCode = "SVL|DHWWE";
        data.B326.shapeSearchCode = "C4|SVL|LHL|C4|DHWWE|P";
        data.B416.shapeSearchCode = "DHWWE|LVL";
        data.B419.shapeSearchCode = "DHWWE|QCNW";
        data.B435.shapeSearchCode = "WWE|QCNW|DHWWE|HCE";
        data.B481.shapeSearchCode = "DHWWE|QCNW";
        data.B483.shapeSearchCode = "LHL|C4|DHWWE";
        data.B489.shapeSearchCode = "C4|DHWWE|QCNW";
        data.B506.shapeSearchCode = "HCN|HCS|DHWWE|QCNW|HCN";
        data.B528.shapeSearchCode = "DHWWE";
        data.B537.shapeSearchCode = "C4|DHWWE|QCNW";
        data.B671.shapeSearchCode = "LHL|DHWWE|LVL|C4";
        data.B686.shapeSearchCode = "LHL|C4|DHWWE|QCNW";
        data.B1087.shapeSearchCode = ["DHWWE|SHL|LVL", "DHWWE|SC"];
        data.B1137.shapeSearchCode = "LHL|C4|SVL|C4|DHWWE";
        data.B6109.shapeSearchCode = "DHWWE|DHWWE";

        //double half-wave north-south
        data.B151.shapeSearchCode = "WNS|DHWNS|LVL";
        data.B236.shapeSearchCode = "WNS|C8|DHWNS";
        data.B285.shapeSearchCode = "C8|DHWNS";
        data.B494.shapeSearchCode = "DHWNS|LHL|WNS";
        data.B612.shapeSearchCode = "WNS|DHWNS";
        data.B724.shapeSearchCode = "LVL|DHWNS|HWS|LVL";
        data.B754.shapeSearchCode = "DHWNS|LHL";
        data.B1021.shapeSearchCode = "WNS|C8|DHWNS|DLN";
        data.B1056.shapeSearchCode = "C8|DOTCOM|DOTCOM|DHWNS|DHWNS";
        data.B1089.shapeSearchCode = "WNS|DHWNS|DLN";

        // Character data is already built-in to bliss-svg-builder - no need to extend
        // Save to blissData for local shape search functionality
        blissData = data;
        this.extendedDataIsFetched = true;

        this.selectorShapes = [
          //heart, ear, animal, alpha, num, WWE, parent, father, mother, QCNW, HCN, QCNE
          [
            "H:0,8=>H",
            "E:0,8=>E",
            "S2:0,9;DC2:4,9;AA2N:0,13;AA2S:4,13=>IND",
            "Xa:-0.5,-2;Xb:2.4,-2;Xc:5.2,-2=>ALPHA",
            "B10:0.25,-2;B11:3,-2;B12:5.75,-2=>NUM",
            "DOT:0,12;COMMA:2,12=>DOTCOM",
            "B699=>QM",
            "W8E:0,8=>WWE",
            "W8N:0,11=>WNS",
            "QC4NW:0,10=>QCNW",
            "HC8N:0,10=>HCN",
            "QC4NE:0,10=>QCNE",
            "S8:0,8=>S8",
            "S4:0,10=>SS",
          ],
          [
            "B473=>B473",
            "B374=>B374",
            "B392=>B392",
            "B500=>B500",
            "B312=>B312",
            "B455=>B455",
            "HW8N:0,11=>HWN",
            "HW8W:0,8;HW8E:2,8=>DHWWE",
            "HW8N:0,10;HW8S:0,12=>DHWNS",
            "HC8W:0,8=>HCW",
            "OC8NW:0,8=>OC",
            "HC8E:0,8=>HCE",
            "B1094=>IS",
            "R48:0,8=>VR",
          ],
          [
            "B570:0,-2=>BODY",
            "B122:0,-2=>B122",
            "B548=>B548",
            "B513=>B513",
            "B433=>B433",
            "B710=>B710",
            "HW8S:0,11=>HWS",
            "HW8W:0,8=>HWW",
            "HW8E:0,8=>HWE",
            "QC4SW:0,10=>QCSW",
            "HC8S:0,10=>HCS",
            "QC4SE:0,10=>QCSE",
            "VL6:0,9;HL6:0,15;VL6:6,9=>OS",
            "R84:0,10=>HR",
          ],
          [
            "VL8:0,8=>LVL",
            "DL4N:0,10=>DLN",
            "DC4:0,10;VL4:2,10=>SDC",
            "B423=>B423",
            "B894=>CWVL",
            "B148:0,-2=>B148",
            "RA4NW:0,10=>RANW",
            "AA4N:0,10=>AN",
            "RA4NE:0,10=>RANE",
            "LARR8W:0,10=>ARRW",
            "LARR8E:0,10=>ARRE",
            "LARR8N:0,8=>ARRN",
            "OR24E:1,12;OR42N:0,9=>WOR",
            "OR24N:1,12;OR42E:0,9=>NOR",
          ],
          [
            "VL3:0,10.5=>SVL",
            "DL4S:0,10=>DLS",
            "DC4:0,10=>DC",
            "B376=>B376",
            "B700:0,-2=>B700",
            "B303=>B303",
            "AA4W:0,10=>AW",
            "P3E:0,12;P3W:4.5,9;P3N:3,13.5;P3S:0,9=>P",
            "AA4E:0,10=>AE",
            "AT4N:0,10=>TN",
            "RT4NW:0,9;RT4SE:2,11=>DRT",
            "LARR8S:0,8=>ARRS",
            "DL3S:0,11.5;VL2:0,11.5;HL2:0,11.5;DL3N:3,9.5;HL2:4,9.5;VL2:6,9.5=>DARR",
            "QC4SW:1.5,11.5;QC4NE:1.5,8.5;RA3N:0,11;RA3S:4,11.5=>RARR",
          ],
          [
            "HL8:0,12=>LHL",
            "HL3:0,12=>SHL",
            "SC4:0,10=>SC",
            "C8:0,8=>C8",
            "C4:0,10=>C4",
            "C2:0,11=>SMC",
            "RA4SW:0,10=>RASW",
            "AA4S:0,10=>AS",
            "RA4SE:0,10=>RASE",
            "AT4S:0,10=>TS",
            "AT4W:0,10=>TW",
            "AT4E:0,10=>TE",
            "",
            "",
          ],
        ];
        this.setSearchStringFromUrl();
      });

    this.updateComplete.then(() => {
      //console.log("blissdictionary is ready");
    });
  }

  focusSearchBox() {
    const textField = this.shadowRoot.getElementById("searchTextField");
    textField.focus();
  }

  handleSearchBoxFocused(e) {
    if (this.shapeSearchIsActive) {
      this.shapeSelectorIsActive = true;
      e.stopPropagation();
    }
    this.updateSearchBoxCaret();
  }

  handleSearchBoxKeydown(e) {
    if (this.shapeSearchIsActive) {
      //if backspace
      if (e.keyCode === 8) {
        this.removeShapeFromShapeSearch();
      }

      e.preventDefault();
    } else {
    }
  }

  updateSearchBoxCaret() {
    let shapeSearchWidth = 0;
    let inputFontSize = 16;

    setTimeout(() => {
      if (this.shapeSearchIsActive) {
        shapeSearchWidth = this.shadowRoot.querySelector("#shapeSearch").offsetWidth;
        inputFontSize = 32;
      }

      const inputEl = this.shadowRoot.getElementById("searchTextField").shadowRoot.querySelector('[part="input"]');
      if (inputEl) {
        inputEl.style.paddingLeft = `${shapeSearchWidth}px`;
        inputEl.style.fontSize = `${inputFontSize}px`;
      }
      this.focusSearchBox();
    });
  }

  handleSearchTextChanged(e) {
    //reset values
    this.hasMore = false;
    this.isLoadingMore = false;
    this.maxHitsExceeded = false;

    if (this.shapeSearchIsActive) {
      this.shapeSelectorIsActive = true;
      e.stopPropagation();
    } else {
      if (e.currentTarget.value === "\u200c") {
        this.searchString = "";
      } else {
        this.searchString = e.currentTarget.value;
      }
    }

    this.updateSearchBoxCaret();
  }

  handleClearButtonClicked(e) {
    if (this.shapeSearchIsActive) {
      this.shapeSelectorIsActive = true;

      this.shapeSearchString = "";
      this.shapeSearchCodes = [];

      this.updateShapeSearchResults();

      e.stopPropagation();
    } else {
      this.searchString = "";
    }
  }

  handleShapeSearchClicked(e) {
    if (this.shapeSearchIsActive) {
      this.searchString = "";
      this.shapeSearchString = "";
      this.shapeSearchCodes = [];
      this.shapeSearchIsActive = false;
      this.shapeSelectorIsActive = false;
    } else {
      this.searchString = "";
      this.shapeSearchString = "";
      this.shapeSearchCodes = [];
      this.shapeSearchIsActive = true;
      this.shapeSelectorIsActive = true;
    }
    this.shadowRoot.getElementById("searchTextField").focus();
    e.stopPropagation();
  }

  handleSearchModeChanged(e) {
    e.stopPropagation();
    const mode = e.target.value;
    this.searchString = "";
    this.shapeSearchString = "";
    this.shapeSearchCodes = [];
    if (mode === "shape") {
      this.shapeSearchIsActive = true;
      this.shapeSelectorIsActive = true;
    } else {
      this.shapeSearchIsActive = false;
      this.shapeSelectorIsActive = false;
    }
    this.shadowRoot.getElementById("searchTextField").focus();
  }

  updated(changedProperties) {
    if (changedProperties.has("searchString") || changedProperties.has("additionalLanguage")) {
      //if (!this.searchRequestIsOngoing && !this.searchTimeoutHandle) {
      if (!this.searchTimeoutHandle) {
        this.searchTimeoutHandle = setTimeout(
          function () {
            clearTimeout(this.searchTimeoutHandle);
            this.searchTimeoutHandle = null;
            this.runSearchQuery();
          }.bind(this),
          300,
        );
      }
      if (this.searchString !== "") {
        this.searchRequestIsOngoing = true;
      }
    }
  }

  runSearchQuery(limit) {
    if (this.searchString === "") {
      this.data = [];
      return;
    }

    const paramObj = {
      q: this.searchString,
    };

    if (this.additionalLanguage) {
      paramObj.lc = this.additionalLanguage;
    }

    let params = new URLSearchParams(paramObj);
    let requestUrl = `${appBase}api/search?${params.toString()}`;

    this.ongoingSearchString = this.searchString;
    this.updateHistoryState();

    if (limit) {
      paramObj.limit = limit;
      params = new URLSearchParams(paramObj);
      requestUrl = `${appBase}api/search?${params.toString()}`;
    }

    this.searchRequestIsOngoing = true;

    //console.log(new Date().toUTCString());
    //console.log("request sent: " + this.searchString);
    fetch(requestUrl)
      .then((resp) => resp.json()) // Transform the data from json to js object
      .then((data) => {
        if (data.searchString !== this.searchString.trim()) {
          //console.log(data.searchString + " disregarded in favor of waiting for " + this.searchString.trim());
          return; //a new request is already on it's way
        }

        this.searchRequestIsOngoing = false;
        const escaped = this.searchString.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const re = new RegExp(escaped, "gi");
        for (const item of data.result) {
          if (item.gloss) {
            item.gloss = buildHighlightedHtml(item.gloss, re);
          }
          if (item.addLangGloss) {
            item.addLangGloss = buildHighlightedHtml(item.addLangGloss, re);
          }
        }
        //console.log(new Date().toUTCString());
        //console.log("response recieved: " + (data.result[0] && data.result[0].bciAvId));
        this.hasMore = data.hasMore;
        this.data = data.result;
        if (!this.isLoadingMore) {
          //console.log("scrolling up");
          this.scrollUpResultDiv();
        } else {
          //console.log("not scrolling up");
          //console.log("not loading more anymore");
          this.isLoadingMore = false;
        }
        if (this.hasMore && this.data.length === 100) {
          this.maxHitsExceeded = true;
        } else {
          this.maxHitsExceeded = false;
        }

        if (this.ongoingSearchString !== this.searchString) {
          this.runSearchQuery(); //if search string has changed since current request, make a new one.
        }
      })
      .catch((error) => {
        this.searchRequestIsOngoing = false;
        console.log(error);
      });
  }

  async firstUpdated() {
    // Forward wheel events from empty margins to the scroll container
    this.shadowRoot.addEventListener('wheel', (e) => {
      const scroller = this.shadowRoot.getElementById('resultAreaContainer');
      if (!scroller) return;
      const rect = scroller.getBoundingClientRect();
      const inside = e.clientX >= rect.left && e.clientX <= rect.right &&
                     e.clientY >= rect.top && e.clientY <= rect.bottom;
      if (!inside) {
        scroller.content.scrollTop += e.deltaY;
        e.preventDefault();
      }
    }, { passive: false });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("popstate", this._handlePopstate);
    if (this.searchTimeoutHandle) {
      clearTimeout(this.searchTimeoutHandle);
      this.searchTimeoutHandle = null;
    }
  }

  // Force wa-input to display the given query. After user interaction,
  // wa-input tracks its own internal "dirty" state and may ignore Lit's
  // .value binding. We directly write to the native <input> inside
  // wa-input's shadow DOM to guarantee the visible value is updated.
  _syncSearchInput(query) {
    this.searchString = query;
    this.updateComplete.then(() => {
      const searchField = this.shadowRoot.getElementById("searchTextField");
      if (!searchField) return;
      // Direct access to the native input bypasses wa-input's dirty-state tracking
      const nativeInput = searchField.shadowRoot?.querySelector('[part="input"]');
      if (nativeInput) {
        nativeInput.value = query;
      } else {
        // Fallback: reset trick via the public property setter
        searchField.value = "";
        searchField.value = query;
      }
    });
  }

  navigateToQuery(query) {
    const url = `${appBase}?q=${encodeURIComponent(query)}`;
    history.pushState({}, "", url);
    this.data = [];
    this.searchRequestIsOngoing = true;
    this._syncSearchInput(query);
    this.scrollUpResultDiv();
  }

  async fetchBlissPng(options) {
    const response = await fetch(`${appBase}api/bliss-png`, {
      method: "POST",
      headers: {
        Accept: "image/png",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(options),
    });

    if (!response.ok) {
      //console.log(response.statusText);
      throw response;
    } else {
      //console.log("fetch ok: " + response.status);
    }

    return response;
  }
  async fetchBlissSvg(options) {
    const response = await fetch(`${appBase}api/bliss-svg`, {
      method: "POST",
      headers: {
        Accept: "image/svg+xml",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(options),
    });

    if (!response.ok) {
      //console.log(response.statusText);
      throw response;
    }

    return response;
  }

  handleShapeButtonClicked(e) {
    const item = e.currentTarget.item;
    const [shapeCode, searchCode] = item.split("=>");

    if (!this.shapeSearchString) {
      this.shapeSearchString = shapeCode;
    } else {
      this.shapeSearchString += `/${shapeCode}`;
    }

    this.shapeSearchCodes.push(searchCode);

    this.updateShapeSearchResults();
  }

  updateShapeSearchResults() {
    //let ids = [];
    let matches = [];

    const countOccurrences = (arr, val) => arr.reduce((a, v) => (v === val ? a + 1 : a), 0);

    let count = 0;
    for (const sc of this.shapeSearchCodes) {
      const numberOfOccurences = countOccurrences(this.shapeSearchCodes, sc);

      let re;
      if (numberOfOccurences === 1) {
        re = new RegExp(`(^|\\|)${sc}(\\||$)`);
      } else {
        re = new RegExp(`(^|\\|)${sc}(\\||$)([\\w\\|]*${sc}(\\||$)){${numberOfOccurences - 1}}`);
      }

      if (count === 0) {
        for (const key in blissData) {
          if (key[0] !== "B") continue;
          const codeOrCodes = blissData[key].shapeSearchCode;

          if (typeof codeOrCodes === "string") {
            const code = codeOrCodes;
            if (re.test(code)) {
              matches.push({
                id: key,
                codes: [code],
              });
            }
          } else if (Array.isArray(codeOrCodes)) {
            const codes = codeOrCodes;
            const matchingCodes = [];

            for (const code of codes) {
              if (re.test(code)) {
                matchingCodes.push(code);
              }
            }

            if (matchingCodes.length > 0) {
              matches.push({
                id: key,
                codes: matchingCodes,
              });
            }
          }
        }
      } else {
        const tempMatches = [...matches];
        matches = [];

        for (const match of tempMatches) {
          const key = match.id;
          const codes = match.codes;

          const matchingCodes = [];

          for (const code of codes) {
            if (re.test(code)) {
              matchingCodes.push(code);
            }
          }

          if (matchingCodes.length > 0) {
            matches.push({
              id: key,
              codes: matchingCodes,
            });
          }
        }
      }
      count++;
    }

    matches.sort((a, b) => {
      let aLength = (a.codes[0].match(/\|/g) || []).length;
      let bLength = (b.codes[0].match(/\|/g) || []).length;
      aLength -= a.codes[0].match(/(\|P|P\|)/) ? 0.5 : 0;
      bLength -= b.codes[0].match(/(\|P|P\|)/) ? 0.5 : 0;
      return aLength - bLength;
    });

    const sortedIds = matches.map((m) => m.id);

    if (sortedIds.length === 1) {
      this.searchString = `${sortedIds[0]}|`; //needed for api call to be interpreted correct
    } else {
      this.searchString = sortedIds.join("|");
    }

    this.updateSearchBoxCaret();
  }

  scrollUpResultDiv() {
    const scroller = this.shadowRoot.getElementById("resultAreaContainer");
    if (scroller?.content) {
      scroller.content.scrollTop = 0;
    }
  }

  handleShowMoreButtonClicked(e) {
    const item = e.detail.item;
    if (item.id) {
      this.navigateToQuery(`B${item.id}`);
    } else if (item.bciAvId) {
      this.navigateToQuery(String(item.bciAvId));
    }
  }

  handleNavigateToWord(e) {
    this.navigateToQuery(String(e.detail.id));
  }

  _getSearchHeader() {
    const q = (this.searchString || "").trim();
    if (!q) return null;
    if (/^\d+$/.test(q)) {
      return `Showing results for BCI ID ${q}`;
    }
    if (/^B\d+$/i.test(q)) {
      return `Showing search results for ${q.toUpperCase()}`;
    }
    return null;
  }

  async handleDownloadButtonClicked(e) {
    const item = e.detail.item;
    const blissCode = item.code;
    const partOfSpeech = item.pos;
    const bciAvId = item.bciAvId;
    const filename = item.filename;

    const s = getAll("dictionary");
    const strokeWidths = getStrokeWidthsForHeight(s.height);

    let options = {
      blisscode: blissCode,
      grid: s.grid,
      height: s.height,
      resolution: s.dpi,
      ...strokeWidths,
    };

    let colorOptions = {};
    if (s.backgroundMode === "transparent") {
      colorOptions.backgroundMode = "";
    } else if (s.backgroundMode === "white") {
      colorOptions = BColor.getColorValuesByColorName("white");
    } else if (s.backgroundMode === "colored") {
      if (this.backgroundModeColorScheme === "Canadian") {
        colorOptions = BColor.getCanadianColorValuesByPos(partOfSpeech);
      } else {
        colorOptions = BColor.getNordicColorValuesByPos(partOfSpeech);
      }
    }
    options = { ...options, ...colorOptions };

    // For small heights, use major color for sky/earth lines
    if (
      s.height === 43 ||
      s.height === 172 ||
      s.height === 66 ||
      s.height === 264
    ) {
      options.gridSkyColor = options.gridMajorColor;
      options.gridEarthColor = options.gridMajorColor;
    }

    const link = document.createElement("a");
    let response;

    try {
      if (s.format === "png") {
        response = await this.fetchBlissPng(options);
      } else if (s.format === "svg") {
        options.title = `${bciAvId}. ${filename}`;
        response = await this.fetchBlissSvg(options);
      } else {
        throw new Error("invalid fileFormat");
      }
    } catch (e) {
      console.log(`failed to generate image! (server response: ${e.status} - ${e.statusText})`);
      return;
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = `${filename}.${s.format}`;
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(url);
    link.remove();
  }

  async handleClipboardButtonClicked(e) {
    const { item, onSuccess } = e.detail;
    const blissCode = item.code;
    const partOfSpeech = item.pos;
    const bciAvId = item.bciAvId;
    const filename = item.filename;

    const s = getAll("dictionary");
    const strokeWidths = getStrokeWidthsForHeight(s.height);

    let options = {
      blisscode: blissCode,
      grid: s.grid,
      height: s.height,
      resolution: s.dpi,
      ...strokeWidths,
    };

    let colorOptions = {};
    if (s.backgroundMode === "transparent") {
      colorOptions.backgroundMode = "";
    } else if (s.backgroundMode === "white") {
      colorOptions = BColor.getColorValuesByColorName("white");
    } else if (s.backgroundMode === "colored") {
      if (this.backgroundModeColorScheme === "Canadian") {
        colorOptions = BColor.getCanadianColorValuesByPos(partOfSpeech);
      } else {
        colorOptions = BColor.getNordicColorValuesByPos(partOfSpeech);
      }
    }
    options = { ...options, ...colorOptions };

    if (
      s.height === 43 ||
      s.height === 172 ||
      s.height === 66 ||
      s.height === 264
    ) {
      options.gridSkyColor = options.gridMajorColor;
      options.gridEarthColor = options.gridMajorColor;
    }

    let response;
    try {
      if (s.format === "png") {
        response = await this.fetchBlissPng(options);
      } else if (s.format === "svg") {
        options.title = `${bciAvId}. ${filename}`;
        response = await this.fetchBlissSvg(options);
      } else {
        throw new Error("invalid fileFormat");
      }
    } catch (err) {
      console.log(`clipboard: failed to generate image! (server response: ${err.status} - ${err.statusText})`);
      return;
    }

    const blob = await response.blob();
    try {
      if (s.format === "png") {
        await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      } else {
        try {
          await navigator.clipboard.write([new ClipboardItem({ "image/svg+xml": blob })]);
        } catch (_) {
          await navigator.clipboard.writeText(await blob.text());
        }
      }
      onSuccess?.();
    } catch (err) {
      console.warn("Clipboard write failed:", err);
    }
  }

  updateHistoryState() {
    const base = window.location.href.split("?")[0];
    if (this.shapeSearchIsActive) {
      window.history.replaceState(
        {},
        document.title,
        `${base}?sq=${encodeURIComponent(this.shapeSearchCodes.join("|"))}`,
      );
    } else {
      window.history.replaceState({}, document.title, `${base}?q=${encodeURIComponent(this.searchString)}`);
    }
  }

  setSearchStringFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const searchString = urlParams.get("q");
    const shapeSearchCodes = urlParams.get("sq");
    if (searchString) {
      this._syncSearchInput(searchString);
    } else if (shapeSearchCodes) {
      this.shapeSearchIsActive = true;
      this.shapeSearchString = shapeSearchCodes
        .split("|")
        .map((code) => {
          const specialCode = this.selectorShapes.flat().find((el) => el.split("=>")[1] === code);
          return specialCode ? specialCode.split("=>")[0] : code;
        })
        .join("/");
      this.shapeSearchCodes = shapeSearchCodes.split("|");
      this.updateShapeSearchResults();
    } else {
      this._syncSearchInput("");
    }
    this.scrollUpResultDiv();
  }

  handleClick(_e) {
    this.shapeSelectorIsActive = false;
  }

  stopPropagation(e) {
    e.stopPropagation();
  }

  handleShapeSelectorClick(e) {
    e.stopPropagation();
  }

  handleRemoveShapeClicked(_e) {
    this.removeShapeFromShapeSearch();
  }

  removeShapeFromShapeSearch() {
    this.shapeSearchString = this.shapeSearchString.replace(/\/?[^/]+$/, "");
    this.shapeSearchCodes.pop();
    this.updateShapeSearchResults();
  }

  getShapeButtonSvg(item) {
    this.shapeButtonSvgs = this.shapeButtonSvgs || [];
    const shapeCode = item.split("=>")[0];

    if (!this.shapeButtonSvgs[shapeCode]) {
      this.shapeButtonSvgs[shapeCode] = buildShapeButtonSvg(shapeCode);
    }

    return this.shapeButtonSvgs[shapeCode];
  }

  get _hasClearableContent() {
    if (this.shapeSearchIsActive) {
      return this.shapeSearchCodes.length > 0;
    }
    return this.searchString !== "";
  }

  highLightSearchString(_stsr) {
    const str = "";
    return str.replace(this.searchString, `<span style="background: #333300">${this.searchString}</span>`);
  }

  handleLoadMoreButtonClicked(e) {
    const command = e.detail.command;
    if (command === "load") {
      this.isLoadingMore = true;
      this.runSearchQuery(100);
    }
  }

  static get styles() {
    return css`
      :host {
        --content-width: 700px;
        font-size: var(--bl-base-font-size, 1em);
        display: flex;
        flex-direction: column;
        flex: 1;
        width: 100%;
        overflow: hidden;
        background: var(--bl-color-background, #faf7f3);
        --wa-color-focus: var(--bl-color-accent, #b89530);
        --wa-color-brand-60: var(--bl-color-accent, #b89530);
        --wa-color-brand-50: var(--bl-color-accent-400, #d2b562);
        --wa-color-brand-40: var(--bl-color-accent-600, #997b25);
      }

      /* Temporary fix: crisp grid lines until bliss-svg-builder ships with it natively */
      svg .grid-line {
        shape-rendering: crispEdges;
      }

      /* Targeted fix for Safari (iOS and macOS) */
      @supports (-webkit-hyphens: none) {
        svg .grid-line {
          shape-rendering: geometricPrecision;
        }
      }

      #searchArea {
        display: flex;
        align-items: center;
        justify-content: center;
        padding-bottom: 15px;
        position: relative;
        border-bottom: 1px solid var(--bl-color-neutral-200, #d5d3cf);
      }

      #searchModeToggle {
        position: absolute;
        left: 3px;
        bottom: 15px; /* align with bottom of searchArea padding */
      }

      #searchModeToggle wa-radio[appearance="button"] {
        width: 56px;
        justify-content: center;
        padding: 2px 0;
      }

      #searchModeToggle wa-radio::part(label) {
        display: inline-flex;
        flex-direction: column;
        align-items: center;
        gap: 1px;
        padding: 0;
        font-size: 0.625em;
        line-height: 1;
      }

      #searchModeToggle wa-radio svg {
        width: 18px;
        height: 18px;
      }

      #searchArea > wa-input {
        width: 100%;
        max-width: 350px;
        padding: 0 8px;
        box-sizing: border-box;
      }

      #searchArea > wa-input svg {
        width: 1.25em;
        height: 1.25em;
        vertical-align: middle;
      }

      wa-input::part(form-control-label) {
        position: relative;
        z-index: 1;
        margin-bottom: calc(-0.65em + 2px);
        margin-left: 10px;
        padding: 0 4px;
        background: white;
        font-size: 0.75em;
        color: rgba(0, 0, 0, 0.6);
        width: fit-content;
        transition: color 150ms ease;
      }

      wa-input:focus-within::part(form-control-label) {
        color: var(--bl-color-accent, #b89530);
      }

      wa-input:focus-within::part(base) {
        outline-color: var(--bl-color-accent, #b89530);
        border-color: var(--bl-color-accent, #b89530);
      }

      wa-input::part(input)::-webkit-search-decoration,
      wa-input::part(input)::-webkit-search-cancel-button,
      wa-input::part(input)::-webkit-search-results-button,
      wa-input::part(input)::-webkit-search-results-decoration {
        -webkit-appearance: none;
      }

      wa-input::part(input)::-ms-clear,
      wa-input::part(input)::-ms-reveal {
        display: none;
        width: 0;
        height: 0;
      }

      #clearButton {
        color: rgba(0, 0, 0, 0.38);
      }

      #clearButton::part(base) {
        padding: 0;
        min-width: 0;
        min-height: 0;
        height: 100%;
        overflow: hidden;
      }

      .hidden {
        visibility: hidden;
      }

      #shapeSearch {
        height: 56px;
        display: inline-block;
        position: absolute;
        left: 220px;
        pointer-events: none;
      }

      #shapeSearch > svg {
        margin-top: 14px;
        height: 38px;
        width: auto;
      }

      #shapeSelector {
        position: fixed;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        height: 300px;
        background: var(--bl-color-neutral-700, #3e3b38);
        width: calc(100% - 12px);
        max-width: 688px;
        border-top: 4px solid var(--bl-color-neutral-200, #d5d3cf);
        border-left: 4px solid var(--bl-color-neutral-200, #d5d3cf);
        border-right: 4px solid var(--bl-color-neutral-200, #d5d3cf);
        border-radius: 25px 25px 0 0;
        box-shadow: var(--bl-color-neutral-700, #3e3b38) 0px 0px 1px 1px;
        display: flex;
        flex-direction: column;
        justify-content: space-evenly;
        padding-bottom: 2px;
        z-index: 2;
        overflow: hidden;
      }

      #shapeSelectorLine {
        display: flex;
        justify-content: space-evenly;
      }
      .shapeButton {
        width: 42px;
        height: 42px;
        background: var(--bl-color-neutral-200, #d5d3cf);
        border-radius: 40%;
        box-sizing: border-box;
        border: 2px solid transparent;
      }

      .shapeButton::part(base) {
        padding: 0;
        min-width: 0;
        min-height: 0;
        width: 100%;
        height: 100%;
        border-radius: 40%;
        --wa-focus-ring-width: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent !important;
      }

      .shapeButton svg {
        width: 38px;
        height: 38px;
        margin-top: 3px;
      }

      .shapeButton:hover {
        background: var(--bl-color-neutral-300, #b5b2ad);
        border-color: var(--bl-color-text-on-overlay, #ffffff);
      }

      .shapeButton:active {
        background: var(--bl-color-neutral-400, #8e8b86);
        border-color: var(--bl-color-text-on-overlay, #ffffff);
      }

      .shapeButtonPlaceholder {
        width: 42px;
        height: 42px;
      }

      #removeShapeIcon {
        color: var(--bl-color-neutral-200, #d5d3cf);
        width: 57px;
        height: 57px;
        position: absolute;
        right: 6px;
        bottom: -1px;
      }

      #removeShapeIcon::part(base) {
        padding: 0;
        min-width: 0;
        min-height: 0;
      }

      #removeShapeIcon svg {
        width: 57px;
        height: 57px;
      }

      #mainArea {
        width: 100%;
        max-width: var(--content-width);
        display: flex;
        flex-direction: column;
        margin: 0 auto;
        position: relative;
        view-transition-name: page-content;
      }

      #resultAreaContainer {
        --shadow-color: color-mix(in srgb, var(--bl-color-background, #faf7f3) 85%, black);
        --shadow-size: 1.5rem;
        flex: 1;
        min-height: 0;
        width: 100%;
        background: transparent;
      }

      #resultAreaContainer::part(content) {
        display: flex;
        flex-direction: column;
      }

      #resultAreaContainer blissary-footer {
        margin-top: auto;
        max-width: var(--content-width);
        margin-left: auto;
        margin-right: auto;
        width: 100%;
      }

      #resultAreaContainer.with-shape-selector::part(content) {
        padding-bottom: 300px;
      }

      #resultAreaContainer.with-shape-selector blissary-footer {
        display: none;
      }

      #resultArea {
        display: flex;
        flex-direction: column;
        gap: 14px;
        padding: 14px 3px;
        max-width: var(--content-width);
        margin: 0 auto;
        width: 100%;
        box-sizing: border-box;
      }

      wa-button,
      a {
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        -khtml-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
        -webkit-tap-highlight-color: transparent;
      }

      .highlight {
        border-bottom: 1px solid var(--bl-color-secondary-400, #5eb4a9);
        border-top: 1px solid var(--bl-color-secondary-400, #5eb4a9);
        background: var(--bl-color-secondary-50, #d0e6e3);
      }

      .searchHeader {
        font-size: 0.9em;
        color: var(--bl-color-text-muted, #888);
        padding: 4px 0;
      }

    `;
  }

  render() {
    return html`
      <blissary-navigation
        title="Bliss Dictionary"
        current-path="/dictionary/"
        .items=${[
          { label: "Home", href: "/", icon: homeIcon },
          { label: "Bliss Dictionary", href: "/dictionary/", icon: bookOpenIcon, disabled: true },
          { label: "Bliss Maker", href: "/maker/", icon: penToolIcon },
          { label: "Assets", href: "/assets", icon: downloadIcon },
          { label: "Resources", href: "/resources", icon: globeIcon },
          { label: "About", href: "/about", icon: infoIcon },
          { label: "Contact", href: "/contact", icon: mailIcon },
        ]}
      ></blissary-navigation>
      <div id="mainArea">
        <div id="searchArea">
          <wa-radio-group id="searchModeToggle" orientation="horizontal" value="${this.shapeSearchIsActive ? "shape" : "text"}" @change="${this.handleSearchModeChanged}" @click="${this.stopPropagation}">
            <wa-radio value="text" appearance="button">${unsafeHTML(textSearchIcon)} Text</wa-radio>
            <wa-radio value="shape" appearance="button">${unsafeHTML(shapeSearchIcon)} Shape</wa-radio>
          </wa-radio-group>
          <wa-input
            id="searchTextField"
            type="search"
            autocomplete="off"
            inputmode="${this.shapeSearchIsActive ? "none" : "search"}"
            label="${this.shapeSearchIsActive ? "Shape search" : "Text search"}"
            .value="${
              this.shapeSearchIsActive
                ? this.shapeSelectorIsActive || this.shapeSearchString
                  ? "\u200c"
                  : ""
                : this.searchString
            }"
            @input="${this.handleSearchTextChanged}"
            @focus="${this.handleSearchBoxFocused}"
            @click="${this.handleSearchBoxFocused}"
            @keydown="${this.handleSearchBoxKeydown}"
          >
            <span slot="start" .innerHTML=${searchIcon}></span>
            <wa-button id="clearButton" slot="end" appearance="plain" class="${this._hasClearableContent ? "" : "hidden"}" @click="${this.handleClearButtonClicked}">
              <span .innerHTML=${closeIcon}></span>
            </wa-button>
          </wa-input>
          <div id="shapeSearch" class="${this.shapeSearchIsActive ? "" : "hidden"}">
            ${this.shapeSearchString ? buildShapeButtonSvg(this.shapeSearchString) : ""}
          </div>
        </div>
      </div>
      <wa-scroller id="resultAreaContainer" orientation="vertical" class="${this.shapeSearchIsActive && this.shapeSelectorIsActive ? "with-shape-selector" : ""}">
        <div id="resultArea">
            ${
              this.extendedDataIsFetched
                ? html`
                  ${
                    this.searchRequestIsOngoing && this.searchString !== "" && this.data.length === 0
                      ? html`<div style="line-height: 48px; font-size: 1.25em;">Searching...</div>`
                      : ""
                  }
                  ${
                    this.data.length === 0 &&
                    (this.searchString !== "" || this.shapeSearchString !== "") &&
                    !this.searchRequestIsOngoing
                      ? html`<div style="line-height: 48px; font-size: 1.25em;">No results found.</div>`
                      : ""
                  }
                  ${
                    this.shapeSearchIsActive && this.data.length > 0
                      ? html`
                        <div style="line-height: 48px; font-size: 1.25em;">
                          Shape search results are limited to <b>Bliss characters</b>.
                        </div>
                        <div style="line-height: 48px; font-size: 1.25em;">
                          Click the SVG image to list <b>Bliss words</b> containing the character.
                        </div>
                      `
                      : ""
                  }
                  ${(() => {
                    const h = this._getSearchHeader();
                    return h && this.data.length > 0 ? html`<div class="searchHeader">${h}</div>` : "";
                  })()}
                  ${guard([this.data, this.backgroundModeColorScheme], () =>
                    repeat(
                      this.data,
                      (item) => item.id,
                      (item) => html`
                        <bl-result-item
                          .item=${item}
                          .colorScheme=${this.backgroundModeColorScheme}
                          .exportFormat=${this.exportFormat}
                          .searchString=${this.searchString}
                          @show-more=${this.handleShowMoreButtonClicked}
                          @download-requested=${this.handleDownloadButtonClicked}
                          @clipboard-requested=${this.handleClipboardButtonClicked}
                          @navigate-to-word=${this.handleNavigateToWord}
                        ></bl-result-item>
                      `,
                    ),
                  )}
                  ${
                    this.shapeSearchIsActive
                      ? ""
                      : html`
                        ${
                          this.maxHitsExceeded
                            ? html`<div>Reached maximum number of hits.</div>`
                            : html`<bl-load-button
                              @load-button-clicked=${this.handleLoadMoreButtonClicked}
                              .isLoading=${this.isLoadingMore}
                              .hasMore=${this.hasMore}
                              button-text="Load more"
                            ></bl-load-button>`
                        }
                      `
                  }
                `
                : ""
            }
          </div>
          <blissary-footer></blissary-footer>
      </wa-scroller>
      ${
        this.shapeSearchIsActive && this.shapeSelectorIsActive
          ? html`
            <div id="shapeSelector" @click="${this.stopPropagation}">
              ${this.selectorShapes.map(
                (arr) => html`
                  <div id="shapeSelectorLine">
                    ${arr.map(
                      (item) => html`
                        ${
                          item !== ""
                              ? html`
                                <wa-button
                                  class="shapeButton"
                                  appearance="plain"
                                  @click="${this.handleShapeButtonClicked}"
                                  .item="${item}"
                                >
                                  ${this.getShapeButtonSvg(item)}
                                </wa-button>
                              `
                              : html` <div class="shapeButtonPlaceholder"></div> `
                          }
                        `,
                      )}
                    </div>
                  `,
                )}
                <wa-button id="removeShapeIcon" appearance="plain" @click="${this.handleRemoveShapeClicked}">
                  ${iconRemoveShape}
                </wa-button>
              </div>
            `
          : ""
      }
    `;
  }
}

customElements.define("bliss-dictionary", BlissDictionary);
